import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import classnames from 'classnames';
import upperFirst from 'lodash/upperFirst';
import PropTypes from 'prop-types';
import { identity } from 'rambdax';
import translate from '@/lib/intl/translate';
import validateMinMax from '@/utils/validateMinMax';
import NumberFormatInput from './NumberFormatInput';

const MIN = 'min';
const MAX = 'max';

export default function ControlledMinMaxNumberInputs({
  classes,
  disabled,
  id,
  label,
  maxName,
  maxRequired,
  minName,
  minRequired,
  minRules: minRulesProp,
  maxRules,
  name,
  parse,
  prefix,
  size,
  suffix,
  thousandSeparator,
  validationError,
  variant,
}) {
  const {
    control,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext();

  const titleCaseName = upperFirst(name);
  const min = minName || `min${titleCaseName}`;
  const max = maxName || `max${titleCaseName}`;
  const watchMin = watch(min);
  const watchMax = watch(max);

  const validate = React.useCallback(
    () => validateMinMax(watchMin, watchMax, validationError),
    [watchMin, watchMax],
  );

  const minRules = {
    ...minRulesProp,
    validate: {
      ...(minRulesProp?.validate || {}),
      minBeforeMax: validate,
    },
  };
  const inputs = React.useMemo(
    () => [
      {
        name: min,
        label: translate('FORMS_MIN_LABEL'),
        required: minRequired,
        rules: minRules,
        type: MIN,
      },
      {
        name: max,
        label: translate('FORMS_MAX_LABEL'),
        required: maxRequired,
        rules: maxRules,
        type: MAX,
      },
    ],
    [minRules, maxRules, name],
  );

  const handleBlur = React.useCallback(() => {
    trigger([inputs[0].name, inputs[1].name]);
  }, []);

  const suffixFn = React.useCallback(
    (value) => {
      if (typeof suffix === 'function') {
        return suffix(value);
      }

      return suffix;
    },
    [suffix],
  );

  return (
    <FormControl
      className={classnames(classes.formControl, 'w-full')}
      component="fieldset"
    >
      {!!label && (
        <FormLabel
          className="mb-2 text-text-primary type-body4"
          component="legend"
        >
          {label}
        </FormLabel>
      )}
      <div className="flex">
        {inputs.map(
          ({ label: inputLabel, name: inputName, required, rules, type }) => (
            <Controller
              key={type}
              control={control}
              name={inputName}
              rules={rules}
              isRequired={required}
              render={({ field: { onChange, value } }) => {
                const isMin = type === MIN;

                return (
                  <NumberFormatInput
                    allowNegative={false}
                    className="w-1/2 flex-1"
                    disabled={disabled}
                    id={`${id}-${type}`}
                    isNumericString
                    InputProps={{
                      classes: {
                        root: classnames({
                          'rounded-r-none': isMin,
                          'rounded-l-none': !isMin,
                        }),
                        notchedOutline: classnames({
                          'border-r-0': isMin,
                        }),
                      },
                    }}
                    label={inputLabel}
                    onBlur={handleBlur}
                    onChange={({ target: { value: inputValue } }) =>
                      onChange(parse(inputValue))
                    }
                    prefix={prefix}
                    required={required}
                    size={size}
                    suffix={suffixFn(value)}
                    thousandSeparator={thousandSeparator}
                    value={value}
                    variant={variant}
                    {...(!!errors[inputName] && {
                      error: !!errors[inputName]?.message,
                    })}
                  />
                );
              }}
            />
          ),
        )}
      </div>

      {(errors[min]?.message || errors[max]?.message) && (
        <FormHelperText error>
          {errors[min].message || errors[max].message}
        </FormHelperText>
      )}
    </FormControl>
  );
}

ControlledMinMaxNumberInputs.propTypes = {
  classes: PropTypes.shape({
    formControl: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  minName: PropTypes.string,
  maxName: PropTypes.string,
  minRules: PropTypes.object,
  maxRules: PropTypes.object,
  maxRequired: PropTypes.bool,
  minRequired: PropTypes.bool,
  parse: PropTypes.func,
  prefix: PropTypes.string,
  size: PropTypes.string,
  suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  thousandSeparator: PropTypes.bool,
  validationError: PropTypes.string.isRequired,
  variant: PropTypes.string,
};

ControlledMinMaxNumberInputs.defaultProps = {
  classes: {},
  disabled: false,
  label: '',
  maxRules: {},
  minRules: {},
  maxName: '',
  minName: '',
  maxRequired: false,
  minRequired: false,
  parse: identity,
  prefix: '',
  size: 'small',
  suffix: '',
  thousandSeparator: false,
  variant: 'outlined',
};
